// RecoveryConfirm — route /auth/recovery/confirm (public, unauthenticated).
//
// The SPA landing for the emailed recovery link (D15/R17). The link arrives as a
// GET with `?token=`; this page reads that token and issues the
// POST /innovateflow/auth/recovery/confirm {token}, which finalizes the reserved
// recovery code, removes the account's second factor, and globally signs it out.
// The user then signs in with their password and re-enrols MFA. Expired/invalid
// tokens are handled gracefully. iflow palette; light theme only.
import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Loader2, ShieldOff, AlertTriangle } from 'lucide-react'
import { confirmRecovery } from '../../services/api'

type Phase = 'working' | 'done' | 'error'

function errMsg(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { data?: { message?: string; error?: string } } }).response
    const m = resp?.data?.message || resp?.data?.error
    if (m) return m
  }
  return e instanceof Error ? e.message : fallback
}

export default function RecoveryConfirm() {
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const [phase, setPhase] = useState<Phase>('working')
  const [error, setError] = useState('')
  // Guard against React 18 StrictMode double-invoke consuming the (single-use) token twice.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    if (!token) {
      setPhase('error')
      setError('This recovery link is missing its token. Request a new link from the account recovery page.')
      return
    }
    confirmRecovery(token)
      .then(() => setPhase('done'))
      .catch((e) => {
        setError(errMsg(e, 'This recovery link is invalid or has expired. Request a new one.'))
        setPhase('error')
      })
  }, [token])

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-sky-50 via-white to-[#E8EFF4]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-iflow-500 to-iflow-700 mx-auto mb-3 flex items-center justify-center text-white text-xl font-bold">iF</div>
          <h1 className="text-2xl font-bold text-iflow-900">Account recovery</h1>
        </div>

        <div className="bg-white rounded-2xl border border-[rgba(12,74,110,0.12)] p-8 shadow-xl text-center">
          {phase === 'working' && (
            <div className="py-4">
              <Loader2 size={28} className="animate-spin text-iflow-500 mx-auto mb-3" />
              <p className="text-sm text-slate-600">Confirming your recovery request…</p>
            </div>
          )}

          {phase === 'done' && (
            <div>
              <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-3"><ShieldOff size={22} /></div>
              <h2 className="text-lg font-semibold text-iflow-900">Second factor removed</h2>
              <p className="text-sm text-slate-600 mt-2">
                Two-factor authentication has been removed from your account and all sessions were signed out. Sign in with
                your password, then set up MFA again from Account, then Security & Keys.
              </p>
              <Link to="/login" className="inline-block mt-5 py-2.5 px-5 bg-iflow-600 text-white text-sm font-semibold rounded-lg hover:bg-iflow-700">
                Sign in
              </Link>
            </div>
          )}

          {phase === 'error' && (
            <div>
              <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-3"><AlertTriangle size={22} /></div>
              <h2 className="text-lg font-semibold text-iflow-900">Recovery link not valid</h2>
              <p className="text-sm text-slate-600 mt-2">{error}</p>
              <div className="mt-5 flex items-center justify-center gap-3">
                <Link to="/auth/recovery" className="text-sm font-medium text-iflow-600 hover:text-iflow-700">Request a new link</Link>
                <span className="text-slate-300">·</span>
                <Link to="/login" className="text-sm font-medium text-iflow-600 hover:text-iflow-700">Back to sign in</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
